import { useState, useCallback, useMemo, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logAdminAuditSafe } from '@/lib/logging/adminAuditSafe';
import {
  AdminProducer,
  toAdminProducer,
  toProducerPayload,
  listProducersAdmin,
  getProducerAdmin,
  createProducer as createProducerApi,
  updateProducer as updateProducerApi,
  deleteProducer as deleteProducerApi,
  checkIsAdmin,
  adminSetProducerStatus,
  ProducerBusinessStatus,
  VerificationStatus,
} from '@/data/producers';
import { ProducerStatus } from '@/data/admin/mockProducers';

export interface ProducerFilters {
  search: string;
  status: ProducerStatus[];
  verified: boolean | null;
  categories: string[];
  producerStatus: ProducerBusinessStatus[];
  verificationStatus: VerificationStatus[];
}

const initialFilters: ProducerFilters = {
  search: '',
  status: [],
  verified: null,
  categories: [],
  producerStatus: [],
  verificationStatus: [],
};

interface UseAdminProducersOptions {
  disableListFetch?: boolean;
}

export function useAdminProducers(opts: UseAdminProducersOptions = {}) {
  const { disableListFetch = false } = opts;
  const [producers, setProducers] = useState<AdminProducer[]>([]);
  const [filters, setFilters] = useState<ProducerFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check admin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      const adminStatus = await checkIsAdmin();
      setIsAdmin(adminStatus);
    };
    checkAdmin();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdmin();
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch producers when filters change
  useEffect(() => {
    if (disableListFetch) {
      setIsLoading(false);
      setError(null);
      return;
    }

    const fetchProducers = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await listProducersAdmin({
          search: filters.search || undefined,
          status: filters.status.length > 0 ? filters.status : undefined,
          verified: filters.verified,
          categories: filters.categories.length > 0 ? filters.categories : undefined,
          producerStatus: filters.producerStatus.length > 0 ? filters.producerStatus : undefined,
          verificationStatus: filters.verificationStatus.length > 0 ? filters.verificationStatus : undefined,
        });
        
        setProducers(data.map(toAdminProducer));
      } catch (err) {
        console.error('Failed to fetch producers:', err);
        setError('Kunde inte hämta värdar');
        setProducers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducers();
  }, [filters, disableListFetch]);

  const filteredProducers = useMemo(() => {
    // Client-side filtering is now minimal since most is done server-side
    // But we keep the memo for compatibility
    return producers;
  }, [producers]);

  const updateFilter = useCallback(<K extends keyof ProducerFilters>(
    key: K,
    value: ProducerFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.status.length > 0 ||
      filters.verified !== null ||
      filters.categories.length > 0 ||
      filters.producerStatus.length > 0 ||
      filters.verificationStatus.length > 0
    );
  }, [filters]);

  const getProducerById = useCallback(
    async (id: string): Promise<AdminProducer | undefined> => {
      try {
        const producer = await getProducerAdmin(id);
        return producer ? toAdminProducer(producer) : undefined;
      } catch {
        return undefined;
      }
    },
    []
  );

  const addProducer = useCallback(
    async (producer: Omit<AdminProducer, 'id' | 'slug' | 'createdAt' | 'updatedAt'>): Promise<AdminProducer> => {
      const payload = toProducerPayload(producer);
      const created = await createProducerApi(payload);
      const adminProducer = toAdminProducer(created);
      
      // Refresh the list
      setProducers((prev) => [adminProducer, ...prev]);
      
      return adminProducer;
    },
    []
  );

  const updateProducer = useCallback(
    async (id: string, updates: Partial<AdminProducer>): Promise<void> => {
      // Fetch before-snapshot for audit
      let beforeSnapshot: Record<string, any> | undefined;
      try {
        const before = await getProducerAdmin(id);
        if (before) beforeSnapshot = before as Record<string, any>;
      } catch { /* best-effort */ }

      // Convert admin updates to DB format
      const dbUpdates: Record<string, any> = {};
      
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.shortDescription !== undefined) dbUpdates.short_description = updates.shortDescription;
      if (updates.longDescription !== undefined) dbUpdates.description = updates.longDescription;
      if (updates.address !== undefined) dbUpdates.address_text = updates.address;
      if (updates.city !== undefined) dbUpdates.city = updates.city;
      if (updates.region !== undefined) dbUpdates.region = updates.region;
      if (updates.lat !== undefined) dbUpdates.lat = updates.lat;
      if (updates.lng !== undefined) dbUpdates.lng = updates.lng;
      if (updates.categories !== undefined) dbUpdates.categories = updates.categories;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.website !== undefined) dbUpdates.website = updates.website;
      if (updates.instagram !== undefined) dbUpdates.instagram = updates.instagram;
      if (updates.email !== undefined) dbUpdates.email = updates.email;
      if (updates.openingHoursText !== undefined) dbUpdates.opening_hours_text = updates.openingHoursText;
      // cover_image_url and gallery_image_urls removed – media_assets is source of truth
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.verified !== undefined) {
        dbUpdates.verified = updates.verified;
        // Set verified_at when toggling to true, clear when false
        dbUpdates.verified_at = updates.verified ? new Date().toISOString() : null;
        // When verifying, set producer_status to active if currently pending
        if (updates.verified) {
          dbUpdates.producer_status = 'active';
        }
      }
      if (updates.verificationNote !== undefined) dbUpdates.verification_note = updates.verificationNote;

      const updated = await updateProducerApi(id, dbUpdates);
      const adminProducer = toAdminProducer(updated);

      // Log GDPR-safe audit (fire-and-forget)
      const afterSnapshot = updated as Record<string, any>;
      const action = updates.status !== undefined
        ? (updates.status === 'public' ? 'publish' : 'unpublish')
        : updates.verified !== undefined
          ? 'verify'
          : 'update';
      logAdminAuditSafe({
        entityId: id,
        action,
        before: beforeSnapshot,
        after: afterSnapshot,
      });
      
      // Update local state
      setProducers((prev) =>
        prev.map((p) => (p.id === id ? adminProducer : p))
      );
    },
    []
  );

  const deleteProducerById = useCallback(async (id: string): Promise<void> => {
    await deleteProducerApi(id);
    setProducers((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const toggleStatus = useCallback(async (id: string) => {
    const producer = producers.find((p) => p.id === id);
    if (!producer) return;
    
    const newStatus = producer.status === 'public' ? 'draft' : 'public';
    await updateProducer(id, { status: newStatus });
  }, [producers, updateProducer]);

  const toggleVerified = useCallback(async (id: string) => {
    const producer = producers.find((p) => p.id === id);
    if (!producer) return;
    
    await updateProducer(id, { verified: !producer.verified });
  }, [producers, updateProducer]);

  const refetch = useCallback(async () => {
    if (disableListFetch) return;

    setIsLoading(true);
    try {
      const data = await listProducersAdmin({
        search: filters.search || undefined,
        status: filters.status.length > 0 ? filters.status : undefined,
        verified: filters.verified,
        categories: filters.categories.length > 0 ? filters.categories : undefined,
        producerStatus: filters.producerStatus.length > 0 ? filters.producerStatus : undefined,
        verificationStatus: filters.verificationStatus.length > 0 ? filters.verificationStatus : undefined,
      });
      setProducers(data.map(toAdminProducer));
    } catch (err) {
      console.error('Failed to refetch producers:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters, disableListFetch]);

  const setProducerStatus = useCallback(async (id: string, status: ProducerBusinessStatus) => {
    // Optimistic update
    const producer = producers.find((p) => p.id === id);
    if (!producer) return;
    
    const oldStatus = producer.producerStatus;
    
    // Optimistic UI update
    setProducers((prev) =>
      prev.map((p) => (p.id === id ? { ...p, producerStatus: status } : p))
    );

    try {
      await adminSetProducerStatus(id, status);
    } catch (err) {
      // Revert on error
      setProducers((prev) =>
        prev.map((p) => (p.id === id ? { ...p, producerStatus: oldStatus } : p))
      );
      throw err;
    }
  }, [producers]);

  return {
    producers: filteredProducers,
    allProducers: producers,
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    getProducerById,
    addProducer,
    updateProducer,
    deleteProducer: deleteProducerById,
    toggleStatus,
    toggleVerified,
    setProducerStatus,
    isLoading,
    isAdmin,
    error,
    refetch,
  };
}
